import * as React from 'react';

import { cn } from '@natu/utils/cn';

import { Typography } from '../Typography/Typography';

interface CardProps extends React.HTMLAttributes<HTMLElement> {
  as?: React.ElementType;
}

const Card = React.forwardRef<HTMLElement, CardProps>(
  ({ className, as: Comp = 'div', ...props }, ref) => (
    <Comp
      ref={ref}
      className={cn(
        'z-10 rounded-2xl bg-white px-4 pb-6 pt-4 shadow-2xl md:px-8 md:py-10',
        className,
      )}
      {...props}
    />
  ),
);
Card.displayName = 'Card';

const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('mb-4 flex flex-col space-y-1.5', className)} {...props} />
  ),
);
CardHeader.displayName = 'CardHeader';

const CardTitle = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, children, ...props }, ref) => (
    <Typography
      variant="display-xs"
      component="p"
      className={cn('font-semibold leading-normal', className)}
      ref={ref}
      {...props}
    >
      {children}
    </Typography>
  ),
);
CardTitle.displayName = 'CardTitle';

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <Typography
    variant="text-sm"
    ref={ref}
    className={cn('text-muted-foreground', className)}
    {...props}
  />
));
CardDescription.displayName = 'CardDescription';

const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('mb-8 mt-4', className)} {...props} />
  ),
);
CardContent.displayName = 'CardContent';

const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('mt-8 flex items-center', className)} {...props} />
  ),
);
CardFooter.displayName = 'CardFooter';

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent };
